import {
  Alert,
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { CategoryBarChart } from '../../shared/charts/CategoryBarChart';
import { StackedShareBar } from '../../shared/charts/StackedShareBar';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { formatMoney, type MoneyLanguage } from '../../shared/format/money';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { SectionCard } from '../../shared/surface/SectionCard';
import { reportDrilldownHref } from '../reports/ReportDrilldowns';
import type { ReportKind } from '../reports/types';
import { DashboardFilters } from './DashboardFilters';
import { KpiGrid } from './KpiGrid';
import { defaultMockDashboardFilters } from './mockRepository';
import { dashboardRepositoryToken, defaultDashboardFilters } from './repository';
import { TrendChart } from './TrendChart';
import { compactFormat } from './trendSeries';
import type {
  DashboardCurrencyTotals,
  DashboardData,
  DashboardFilters as DashboardFilterValues,
  DashboardRanking,
  DashboardSummary,
} from './types';

export function DashboardPage({ dataSource }: { readonly dataSource: DataSource }) {
  const repository = useRepository(dashboardRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: MoneyLanguage & TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const baseFilters = dataSource === 'mock' ? defaultMockDashboardFilters : defaultDashboardFilters;
  const scopedFilters = useMemo(() => {
    const scope = auth.status === 'authenticated' ? auth.principal.scope : null;
    return {
      ...baseFilters,
      partnerId: scope?.partnerId ?? baseFilters.partnerId,
      agentId: scope?.agentId ?? baseFilters.agentId,
    };
  }, [auth, baseFilters]);
  const [filters, setFilters] = useState<DashboardFilterValues>(scopedFilters);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    async (signal) => {
      const [summary, trends, topGames, topAffiliates] = await Promise.all([
        repository.getSummary(filters, signal),
        repository.getTrends(filters, signal),
        repository.getTopGames(filters, signal),
        repository.getTopAffiliates(filters, signal),
      ]);
      return { summary, trends, topGames, topAffiliates };
    },
    [repository, filters, refreshKey],
    isDashboardEmpty,
  );
  const principal = auth.status === 'authenticated' ? auth.principal : null;

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('dashboard.subtitle')}
        title={t('dashboard.title')}
      />
      <DashboardFilters
        lockedAgentId={principal?.scope.agentId ?? null}
        lockedPartnerId={principal?.scope.partnerId ?? null}
        onApply={setFilters}
        resetValue={scopedFilters}
        value={filters}
      />
      <PageState
        emptyMessage={t('dashboard.empty')}
        error={toPageError(resource.error, t('dashboard.error'))}
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <DashboardContent
            data={resource.data}
            filters={filters}
            language={language}
            showApiHealth={principal?.role === 'SUPER_ADMIN'}
          />
        )}
      </PageState>
    </Box>
  );
}

function DashboardContent({
  data,
  filters,
  language,
  showApiHealth,
}: {
  readonly data: DashboardData;
  readonly filters: DashboardFilterValues;
  readonly language: MoneyLanguage & TimeLanguage;
  readonly showApiHealth: boolean;
}) {
  const { t } = useTranslation();
  const reportingAsOf = formatTimestamp(
    data.summary.meta.reportingAsOf,
    data.summary.meta.timezone,
    language,
  ).display;

  return (
    <Stack spacing={3}>
      {data.summary.meta.stale ? (
        <Alert severity="warning">{t('dashboard.stale', { date: reportingAsOf })}</Alert>
      ) : (
        <Typography color="text.secondary" variant="body2">
          {t('dashboard.reportingAsOf', { date: reportingAsOf })}
        </Typography>
      )}
      <KpiGrid filters={filters} summary={data.summary} />
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 2fr) minmax(18rem, 1fr)' },
        }}
      >
        <TrendChart trends={data.trends} />
        <Stack spacing={2}>
          <WalletCard currencies={data.summary.currencies} filters={filters} language={language} />
          <WithdrawalCard filters={filters} summary={data.summary} language={language} />
          {showApiHealth ? <ApiHealthCard summary={data.summary} language={language} /> : null}
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        <RankingTable
          filters={filters}
          kind="games"
          language={language}
          ranking={data.topGames}
          title={t('dashboard.topGames')}
          type="game"
        />
        <RankingTable
          filters={filters}
          kind="user-affiliate-by-game"
          language={language}
          ranking={data.topAffiliates}
          title={t('dashboard.topAffiliates')}
          type="affiliate"
        />
      </Box>
    </Stack>
  );
}

function WalletCard({
  currencies,
  filters,
  language,
}: {
  readonly currencies: readonly DashboardCurrencyTotals[];
  readonly filters: DashboardFilterValues;
  readonly language: MoneyLanguage;
}) {
  const { t } = useTranslation();
  const categories = currencies.map((currency) => currency.currencyCode);

  return (
    <SectionCard
      action={
        <Button component={Link} size="small" to={reportHref('wallets', filters, { status: '' })}>
          {t('dashboard.wallet.view')}
        </Button>
      }
      title={t('dashboard.wallet.title')}
    >
      {categories.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t('dashboard.empty')}
        </Typography>
      ) : (
        <StackedShareBar
          ariaLabel={t('dashboard.charts.walletLabel')}
          categories={categories}
          rowHeader={t('dashboard.filters.currency')}
          series={[
            {
              labels: currencies.map((currency) =>
                formatMoney(currency.walletAvailable, currency.currencyCode, language),
              ),
              name: t('dashboard.wallet.available'),
              values: currencies.map((currency) => Number(currency.walletAvailable)),
            },
            {
              labels: currencies.map((currency) =>
                formatMoney(currency.walletReserved, currency.currencyCode, language),
              ),
              name: t('dashboard.wallet.reserved'),
              values: currencies.map((currency) => Number(currency.walletReserved)),
            },
          ]}
          tableCaption={t('dashboard.charts.walletTable')}
        />
      )}
      {/*
        The chart shows each currency's available/reserved proportion; these lines carry the amounts. A
        proportion alone is not a balance, and before this redesign these figures were plain text — the
        chart must not be the only way to read them, least of all on touch, where there is no hover.
      */}
      <Stack spacing={0.75} sx={{ mt: categories.length === 0 ? 0 : 2 }}>
        {currencies.map((currency) => (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            key={currency.currencyCode}
            spacing={{ sm: 1.5 }}
            sx={{ justifyContent: 'space-between' }}
          >
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              {currency.currencyCode}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t('dashboard.wallet.available')}:{' '}
              {formatMoney(currency.walletAvailable, currency.currencyCode, language)} ·{' '}
              {t('dashboard.wallet.reserved')}:{' '}
              {formatMoney(currency.walletReserved, currency.currencyCode, language)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </SectionCard>
  );
}

function WithdrawalCard({
  filters,
  summary,
  language,
}: {
  readonly filters: DashboardFilterValues;
  readonly summary: DashboardSummary;
  readonly language: MoneyLanguage;
}) {
  const { t } = useTranslation();
  return (
    <SectionCard
      action={
        <Chip
          color={summary.withdrawalExceptionCount > 0 ? 'warning' : 'success'}
          label={summary.withdrawalExceptionCount}
          size="small"
        />
      }
      title={t('dashboard.withdrawals.title')}
    >
      {summary.currencies.map((currency) => (
        <Typography key={currency.currencyCode} sx={{ mt: 1 }} variant="body2">
          {t('dashboard.withdrawals.unknown')}:{' '}
          {formatMoney(currency.withdrawalUnknown, currency.currencyCode, language)}
        </Typography>
      ))}
      <Button
        component={Link}
        size="small"
        sx={{ mt: 1.5 }}
        to={reportHref('withdrawals', filters, { status: '' })}
      >
        {t('dashboard.withdrawals.investigate')}
      </Button>
    </SectionCard>
  );
}

function reportHref(
  kind: ReportKind,
  filters: DashboardFilterValues,
  overrides: Readonly<Record<string, string>> = {},
): string {
  return reportDrilldownHref(kind, { ...filters, ...overrides });
}

function ApiHealthCard({
  summary,
  language,
}: {
  readonly summary: DashboardSummary;
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  const health = summary.apiHealth;
  return (
    <SectionCard
      action={
        <Button component={Link} size="small" to="/api-operations">
          {t('dashboard.apiHealth.title')}
        </Button>
      }
      title={t('dashboard.apiHealth.title')}
    >
      {health === null ? (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {t('dashboard.apiHealth.unavailable')}
        </Alert>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Chip
            color={
              health.status === 'operational'
                ? 'success'
                : health.status === 'degraded'
                  ? 'warning'
                  : 'error'
            }
            label={t(`dashboard.apiHealth.${health.status}`)}
            size="small"
            sx={{ alignSelf: 'flex-start' }}
          />
          <Typography variant="body2">
            {t('dashboard.apiHealth.latency', { value: health.latencyMs })}
          </Typography>
          <Typography variant="body2">
            {t('dashboard.apiHealth.successRate', { value: health.successRate })}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {t('dashboard.apiHealth.checkedAt', {
              date: formatTimestamp(health.checkedAt, summary.meta.timezone, language).display,
            })}
          </Typography>
        </Stack>
      )}
    </SectionCard>
  );
}

function RankingTable({
  filters,
  kind,
  language,
  ranking,
  title,
  type,
}: {
  readonly filters: DashboardFilterValues;
  readonly kind: 'games' | 'user-affiliate-by-game';
  readonly language: MoneyLanguage;
  readonly ranking: DashboardRanking;
  readonly title: string;
  readonly type: 'game' | 'affiliate';
}) {
  const { t } = useTranslation();
  // One chart per currency. Bar length is a comparison, and amounts in different currencies are not
  // comparable — plotting THB beside USD on one axis made a large USD row look negligible next to a THB
  // row worth a similar amount. Grouping means every bar in a chart shares a unit. The visible table
  // below still lists every row together, in the API's own ranking order.
  const byCurrency = new Map<string, DashboardRanking['rows'][number][]>();
  for (const row of ranking.rows) {
    byCurrency.set(row.currencyCode, [...(byCurrency.get(row.currencyCode) ?? []), row]);
  }

  return (
    <SectionCard title={title}>
      {kind === 'games'
        ? [...byCurrency].map(([currencyCode, rows]) => (
            <CategoryBarChart
              ariaLabel={t('dashboard.charts.topGamesLabelForCurrency', {
                currency: currencyCode,
              })}
              categories={rows.map(
                (row) => [row.name, row.code].filter(Boolean).join(' · ') || t('common.unknown'),
              )}
              formatValue={(value) => compactFormat(language).format(value)}
              key={currencyCode}
              labels={rows.map((row) => formatMoney(row.playAmount, row.currencyCode, language))}
              rowHeader={t('dashboard.rankings.game')}
              // The visible ranking table below carries the same figures with more columns, so it is the
              // better text alternative; a hidden second copy would be read out twice.
              tableProvidedByCaller
              tableCaption={t('dashboard.charts.topGamesTable')}
              valueHeader={t('dashboard.rankings.playAmount')}
              values={rows.map((row) => Number(row.playAmount))}
            />
          ))
        : null}
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table aria-label={title} size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 220 }}>
                {t(type === 'game' ? 'dashboard.rankings.game' : 'dashboard.rankings.affiliate')}
              </TableCell>
              <TableCell align="right">{t('dashboard.kpis.transactions')}</TableCell>
              <TableCell align="right">{t('dashboard.rankings.playAmount')}</TableCell>
              <TableCell align="right">{t('dashboard.rankings.commission')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ranking.rows.map((row) => {
              const label =
                type === 'game'
                  ? [row.name, row.code].filter(Boolean).join(' · ')
                  : [row.username, row.name].filter(Boolean).join(' · ');
              return (
                <TableRow key={`${row.id}-${row.currencyCode}`}>
                  <TableCell sx={{ minWidth: 220 }}>
                    <MuiLink
                      component={Link}
                      to={reportHref(kind, filters, {
                        [type === 'game' ? 'gameId' : 'memberId']: row.id,
                        ...(type === 'game' ? { status: '' } : {}),
                      })}
                    >
                      {label.length === 0 ? t('common.unknown') : label}
                    </MuiLink>
                  </TableCell>
                  <TableCell align="right">{row.transactionCount}</TableCell>
                  <TableCell align="right">
                    {formatMoney(row.playAmount, row.currencyCode, language)}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(row.totalCommission, row.currencyCode, language)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </SectionCard>
  );
}

function isDashboardEmpty(data: DashboardData): boolean {
  return (
    data.summary.transactionCount === 0 &&
    data.summary.currencies.length === 0 &&
    data.trends.points.length === 0 &&
    data.topGames.rows.length === 0 &&
    data.topAffiliates.rows.length === 0
  );
}

function toPageError(
  error: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> | null {
  if (error === null) return null;
  if (typeof error === 'object' && error !== null && 'message' in error && 'requestId' in error) {
    const candidate = error as { readonly message: unknown; readonly requestId: unknown };
    return {
      message: typeof candidate.message === 'string' ? candidate.message : fallback,
      requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
    };
  }
  return { message: fallback, requestId: null };
}
